import { useEffect, useMemo, useRef, useState } from "react";

import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { Skeleton } from "primereact/skeleton";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { getApiTokensClient } from "sj-ui-toolkit/src/client/svcApiTokens";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { IS_DEV_MODE } from "sj-ui-toolkit/src/constants/env";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const buildScimAppUrl = (tenantName: string) => {
  if (IS_DEV_MODE) {
    return `https://api.securityjourney.dev/scim/${tenantName}/v2`;
  }
  return `https://api.securityjourney.com/scim/${tenantName}/v2`;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ScimConnection = () => {
  useBreadcrumb({
    label: "SCIM",
    route: AdminAppRoutes.routes.connections.routes.scim.route,
    display: "page",
  });

  const toast = useRef<Toast>(null);
  const apiTokensClient = getApiTokensClient();
  const tenant = getCurrentSelectedTenant();

  const [scimLoading, setScimLoading] = useState(true);
  const [scimSettings, setScimSettings] = useState<
    {
      tenant?: string;
      uuid?: string;
      secret?: string;
      createdAt?: string;
      label?: string;
      resolvedAt?: string;
    }[]
  >([]);
  const [scimEnabled, setScimEnabled] = useState(false);
  const [scimSecret, setScimSecret] = useState(false);

  useEffect(() => {
    const getScimSettings = async () => {
      const { data, error } = await apiTokensClient.listApiTokensWithEntitlements(["scim"]);
      if (error) {
        console.error("Failed to load SCIM settings", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load SCIM settings. Please try again.",
          life: 5000,
        });
      } else {
        const apiTokens = data?.apiTokens || [];
        if (apiTokens.length) {
          setScimEnabled(true);
          setScimSettings(apiTokens);
        }
      }
      setScimLoading(false);
    };

    getScimSettings();
  }, [apiTokensClient]);

  const currentScimToken = useMemo(() => scimSettings[0], [scimSettings]);

  const resetScimButtonText = useMemo(() => {
    if (currentScimToken) {
      return scimSecret ? "Copy SCIM token" : "Reset SCIM token";
    }
    return scimLoading ? "Generating SCIM token" : "Generate SCIM token";
  }, [currentScimToken, scimLoading, scimSecret]);

  const scimTokenHiddenText = useMemo(() => {
    if (currentScimToken && scimSecret) {
      return "This token will be hidden once you navigate from this page. Copy it now, if you do not copy it now, you will need to reset it to see it again.";
    }
    return "The SCIM token is now hidden. To see the value of the token again, you need to reset it.";
  }, [currentScimToken, scimSecret]);

  const handleCopyApiUrl = () => {
    if (!tenant) return;
    navigator.clipboard.writeText(buildScimAppUrl(tenant));
    toast.current?.show({
      severity: "info",
      summary: "Copied",
      detail: "SCIM API Url copied to clipboard",
    });
  };

  const copyScimToken = async () => {
    if (currentScimToken?.secret) {
      try {
        await navigator.clipboard.writeText(currentScimToken.secret);
        toast.current?.show({
          severity: "info",
          summary: "Copied",
          detail: "SCIM Token copied to clipboard",
        });
      } catch (error) {
        console.error("Failed to copy SCIM token", error);
      }
    }
  };

  const handleGenerateButton = async () => {
    if (currentScimToken && scimSecret && currentScimToken?.secret) {
      // Token is visible — copy it to clipboard
      await copyScimToken();
      return;
    }

    if (currentScimToken && !scimSecret) {
      // Token exists but is hidden — reset: revoke all existing tokens then issue a new one
      setScimLoading(true);
      for (const token of scimSettings) {
        if (!token.uuid) continue;
        const { error } = await apiTokensClient.revokeApiToken(token.uuid);
        if (error) {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: `Failed to reset SCIM token: ${error.message}`,
            life: 5000,
          });
          setScimLoading(false);
          return;
        }
      }
      setScimSettings([]);
    }

    // No token exists (or was just revoked above) — issue a new one
    setScimLoading(true);
    const { data, error } = await apiTokensClient.issueApiToken("SCIM auth token", ["scim"]);
    if (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: `Failed to generate SCIM token: ${error.message}`,
      });
    } else {
      const newToken = { uuid: data?.secret?.split("_")[0] ?? "", secret: data?.secret ?? "" };
      setScimSettings([newToken]);
      setScimSecret(true);
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Generated SCIM token",
      });
    }
    setScimLoading(false);
  };

  const handleDisableScim = async () => {
    if (scimEnabled && scimSettings.length) {
      for (const token of scimSettings) {
        if (!token.uuid) continue;
        const { error } = await apiTokensClient.revokeApiToken(token.uuid);
        if (error) {
          console.error(`Failed to delete SCIM token: ${error.message}`);
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: `Failed to disable SCIM: ${error.message}`,
            life: 5000,
          });
          return;
        }
      }
      setScimSettings([]);
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "SCIM has been disabled and all tokens have been revoked",
      });
    }
    setScimEnabled(false);
  };

  const toggleScim = (value: boolean, event: { originalEvent: { currentTarget: HTMLElement } }) => {
    if (!value && scimEnabled) {
      confirmPopup({
        target: event.originalEvent.currentTarget,
        message: (
          <div>
            <p className="font-semibold text-lg mb-3">Disabling SCIM</p>
            <div className="body-sm space-y-2">
              <p>
                Disabling SCIM will delete all generated tokens. A new token will need to be
                generated if SCIM is enabled at a later time.
              </p>
              <p>Would you like to continue?</p>
            </div>
          </div>
        ),
        icon: "pi pi-exclamation-triangle",
        defaultFocus: "reject",
        acceptClassName: "p-button-danger",
        acceptLabel: "Yes, Disable",
        rejectLabel: "Cancel",
        accept: handleDisableScim,
        reject: () => setScimEnabled(true),
      });
    } else if (value && !scimEnabled) {
      setScimEnabled(true);
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "SCIM has been enabled",
      });
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmPopup />

      <div className="mb-4">
        <h1>SCIM</h1>
      </div>

      <Card>
        <div className="flex justify-end items-center mb-4">
          {scimLoading ? (
            <Skeleton width="3rem" height="1.75rem" borderRadius="1rem" />
          ) : (
            <InputSwitch
              checked={scimEnabled}
              onChange={e => toggleScim(e.value, e as any)}
              disabled={scimLoading}
            />
          )}
        </div>

        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-3">
            <label htmlFor="scimToken" className="block font-medium">
              SCIM Token
            </label>
            {scimLoading ? (
              <>
                <Skeleton height="2.5rem" className="w-full" />
                <Skeleton width="13rem" height="1rem" />
                <Skeleton width="13rem" height="2.5rem" borderRadius="0.375rem" />
              </>
            ) : (
              <>
                <InputText
                  id="scimToken"
                  value={currentScimToken?.secret}
                  placeholder="****************"
                  disabled={!scimSecret}
                  onClick={copyScimToken}
                  className="w-full"
                />
                <p className="body-sm text-secondary italic">{scimTokenHiddenText}</p>
                <Button
                  label={resetScimButtonText}
                  onClick={handleGenerateButton}
                  className="w-52"
                />
              </>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <label htmlFor="scimApiUrl" className="block font-medium">
              SCIM API Url
            </label>
            {scimLoading ? (
              <>
                <Skeleton height="2.5rem" className="w-full" />
                <Skeleton width="13rem" height="2.5rem" borderRadius="0.375rem" />
              </>
            ) : (
              <>
                <InputText
                  id="scimApiUrl"
                  value={tenant ? buildScimAppUrl(tenant) : ""}
                  className="w-full"
                />
                <Button
                  label="Copy SCIM API Url"
                  onClick={handleCopyApiUrl}
                  outlined
                  className="w-52"
                  disabled={!tenant}
                />
              </>
            )}
          </div>
        </div>
      </Card>
    </>
  );
};

export default ScimConnection;
